import React, { useState, useEffect } from "react";
import { Box, Text, Flex, Button, Stack, Image } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
	AppDispatch,
	setPageLoading,
	useErrorHandler,
	RenderTextField,
	RenderPhoneField,
	updateUserDetails,
	SUCCESS_MSG,
	SET_MESSAGE,
	fetchUserDetails,
	BasicDialog,
} from "../index";
import DashnodeLogo from "../../img/Dashnode_logo_with_name.png";
import { resolveLocaleDefaults } from "../utils/localeDefaults";
import "../../pages/Auth/AuthStyles.css";

const MissingInfoPopup: React.FC = () => {
	const dispatch = useDispatch<AppDispatch>();
	const handleError = useErrorHandler();
	const location = useLocation();
	const [isOpen, setIsOpen] = useState(false);
	const [phoneDetails, setPhoneDetails] = useState<any>(null);

	const customerData = useSelector((state: any) => state.customerData);

	useEffect(() => {
		const isShown = sessionStorage.getItem("missingInfoPopupShown");
		const isMyProfile = location.pathname.includes("/myprofile");

		if (
			customerData &&
			Object.keys(customerData).length > 0 &&
			customerData?.email &&
			!isShown &&
			!isMyProfile
		) {
			if (!customerData.phone || !customerData.company_name) {
				setTimeout(() => {
					setIsOpen(true);
				}, 1000);
			}
		}
	}, [customerData, location.pathname]);

	const basicValidation = Yup.object().shape({
		phone: Yup.string().required("Phone number is required"),
		company_name: Yup.string(),
	});

	const onClose = () => {
		sessionStorage.setItem("missingInfoPopupShown", "true");
		setIsOpen(false);
	};

	const onSubmit = async (values: any) => {
		dispatch(
			setPageLoading({
				loading: true,
				loadingText: "Updating your workspace...",
			}),
		);
		try {
			// Fetch fresh user data to merge cleanly
			const response = await fetchUserDetails();
			const existingData = response?.data?.data?.[0] || {};

			const payload: any = {
				...existingData,
				phone: values.phone,
				company_name: values.company_name,
			};

			if (phoneDetails && phoneDetails.countryISO2) {
				const locale = resolveLocaleDefaults(phoneDetails.countryISO2);
				payload.country = `${phoneDetails.countryName} (${phoneDetails.countryISO2})`;
				payload.currency = locale.currency;
				payload.unit = locale.unitSystem;
			}

			// Clean payload
			delete payload.email;
			delete payload.current_password;
			delete payload.new_password;
			delete payload.scrap_saving_cost_enabled;
			delete payload.beta_feature_user;
			delete payload.doc;
			delete payload.name;
			delete payload.signed_source;
			delete payload.logo;

			const res: any = await updateUserDetails(payload);
			if (res.status >= 200 && res.status < 400) {
				dispatch({
					type: SET_MESSAGE,
					payload: {
						case: SUCCESS_MSG,
						data: "Workspace details updated successfully",
					},
				});
				onClose();
			} else {
				handleError("Failed to update workspace details");
			}
		} catch (error: any) {
			handleError(error?.response?.data?.message || "An error occurred");
		} finally {
			dispatch(
				setPageLoading({
					loading: false,
					loadingText: "",
				}),
			);
		}
	};

	const dialogContent = (
		<Box w="100%" p="1rem">
			<Flex flexDir="column" alignItems="center" textAlign="center">
				<Image src={DashnodeLogo} alt="Dashnode" width="160px" mb="1.5rem" />
				<Text fontWeight="600" fontSize="1.5rem" mb=".5rem" color="#000000">
					Set up your workspace
				</Text>
				<Text fontWeight="400" fontSize="1rem" mb="2rem" color="#26203B">
					Add a few details so we can tailor your experience.
				</Text>
			</Flex>

			<Formik
				initialValues={{
					phone: customerData?.phone || "",
					company_name: customerData?.company_name || "",
				}}
				validationSchema={basicValidation}
				onSubmit={onSubmit}
				enableReinitialize
			>
				{(formik) => {
					return (
						<Form id="missingInfoForm" className="missingInfoForm">
							<Stack spacing="1.5rem" width="100%">
								<RenderPhoneField
									name="phone"
									label="Phone Number"
									placeholder=""
									isMandatory={true}
									color="#1A202C"
									labelColor="#26203B"
									labelsize="1rem"
									onPhoneChange={(info: any) => setPhoneDetails(info)}
									inputStyle={{
										width: "100%",
										height: "2.675rem",
										border: "1px solid #0C64E866",
										borderRadius: "0.375rem",
									}}
								/>

								<RenderTextField
									name="company_name"
									label="Company Name"
									type="text"
									placeholder="Enter Company Name"
									color="#1A202C"
									labelColor="#26203B"
									labelsize="1rem"
									variant="Outline"
									border="1px solid #0C64E866"
									height="3rem"
									_placeholder={{ color: "#A0AEC0" }}
									_invalid={{ borderColor: "red.500" }}
								/>

								<Flex
									alignItems="center"
									justifyContent="flex-end"
									width="100%"
									gap="1rem"
									mt="1.5rem"
								>
									<Button
										variant="link"
										color="#0000004D"
										fontWeight={400}
										_hover={{ color: "#0000006D", textDecoration: "none" }}
										onClick={onClose}
										px="3rem"
									>
										Skip for now
									</Button>
									<Button
										className="aceSetupBtn"
										id="ace_setup_btn"
										type="submit"
										width="12rem"
										height="3rem"
										children="Continue"
										bg="#0C64E8"
										_hover={{ bg: "#006DC0" }}
										color="#fff"
									/>
								</Flex>
							</Stack>
						</Form>
					);
				}}
			</Formik>
		</Box>
	);

	return (
		<BasicDialog
			isOpen={isOpen}
			onClose={onClose}
			content={dialogContent}
			size={{ xs: "xs", md: "md", lg: "lg" }}
			width="100%"
		/>
	);
};

export default MissingInfoPopup;
